import React from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, FileDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function getVal(payload, field) {
  if (!payload || !field) return '';
  return payload[field] !== undefined ? String(payload[field]) : '';
}

function buildRows(submissions, columns) {
  return submissions.map(s =>
    columns.map(col => getVal(s.raw_payload, col))
  );
}

export default function ZohoReportExport({ submissions, columns, formName }) {
  const exportCSV = () => {
    try {
      const header = columns.join(',');
      const rows = buildRows(submissions, columns).map(r =>
        r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formName || 'report'}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const exportExcel = () => {
    try {
      const rows = buildRows(submissions, columns);
      const wsData = [columns, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');
      XLSX.writeFile(wb, `${formName || 'report'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
      doc.setFontSize(14);
      doc.text(formName || 'Report', 14, 15);
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString()} | Records: ${submissions.length}`, 14, 22);

      const rows = buildRows(submissions, columns);

      autoTable(doc, {
        head: [columns],
        body: rows,
        startY: 26,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [30, 30, 30] },
      });

      doc.save(`${formName || 'report'}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported');
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="report-export-btn" className="gap-1">
          <Download className="h-3.5 w-3.5" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportCSV} data-testid="export-csv">
          <FileDown className="h-4 w-4 mr-2" />CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportExcel} data-testid="export-excel">
          <FileSpreadsheet className="h-4 w-4 mr-2" />Excel
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportPDF} data-testid="export-pdf">
          <FileText className="h-4 w-4 mr-2" />PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
